// src/components/dashboard/EngagementReadinessGate.tsx
//
// Blocks fulfillment UI for an engagement until its outcome-type
// requirements are met. Matches DashboardDetectionFindings.tsx's
// established empty-state/card conventions exactly — dashed border for
// the blocking state, plain sentence explaining what's missing, one
// clear action, no generic "connectors" framing. Per the reference
// design (buildlog IxVzKfwsbfK0xZPVDLf8): this is scoped per-engagement,
// not a standalone Connectors page.
//
// UPDATED — children can now optionally be a render-prop function
// receiving { missing }, so the PARENT can also react to what this
// engagement actually requires — specifically, DashboardEngagementDetail.tsx
// needed to know whether to render MarqetaConnectForm at all, and was
// previously rendering it unconditionally on EVERY engagement (including
// an NDA engagement with no Marqeta dependency whatsoever), because this
// component never surfaced its own readiness result outward. Plain
// ReactNode children still work unchanged — this is additive, not a
// breaking change to any other caller of this component.
//
// FIXED — REQUIREMENT_ROUTES.marqeta_connector pointed at
// /dashboard/connect/marqeta as a plain <a href>, but that path is a
// POST-only backend endpoint (dashboard.ts's dashboardRouter.post(...)),
// not a page — visiting it directly returns an empty SPA shell with no
// matching frontend route. Confirmed live this session. Removed the
// direct link entirely for the marqeta_connector case; the real
// connect action lives in MarqetaConnectForm's own Dialog (rendered by
// the parent now that it knows to render it — see above), not a
// navigable URL. repo_access's route is left as-is since that connector
// kind doesn't exist yet and this wasn't independently confirmed broken
// the same way.
//
// FIXED (this pass) — the render-prop was previously only invoked on
// the READY path (`if (result?.ready) return <>{renderChildren()}</>`).
// This meant a NOT-ready engagement — the exact case where a merchant
// actually needs to see a connect form to resolve the missing
// requirement — never gave the parent a chance to render anything
// beyond this component's own generic blocking message. Confirmed live:
// MarqetaConnectForm disappeared entirely once an engagement was
// correctly identified as not-ready, the opposite of what should
// happen. The render-prop (when children is a function) now also fires
// on the not-ready path, rendered directly below the missing-requirement
// list, so the parent's connector UI appears alongside the blocking
// explanation rather than only after the engagement is already ready
// (at which point there'd be nothing left to connect).

import React, { useState, useEffect } from 'react';
import { authedFetch } from '../../services/dashboardApi';
import { LinkIcon, Loader2Icon } from 'lucide-react';

interface ReadinessRequirement {
  kind: 'marqeta_connector' | 'repo_access';
  label: string;
}

interface ReadinessResult {
  ready: boolean;
  missing: ReadinessRequirement[];
}

// Where each requirement kind sends the merchant to resolve it. Only
// used now for kinds whose resolution genuinely IS a separate page
// (repo_access, not yet built). marqeta_connector's real action is a
// Dialog rendered by the parent, not a navigable route — see header.
const REQUIREMENT_ROUTES: Partial<Record<ReadinessRequirement['kind'], string>> = {
  repo_access: '/dashboard/connect/repo',
};

interface EngagementReadinessGateProps {
  engagementRef: string;
  children: React.ReactNode | ((result: { missing: ReadinessRequirement[] }) => React.ReactNode);
}

export const EngagementReadinessGate: React.FC<EngagementReadinessGateProps> = ({
  engagementRef,
  children,
}) => {
  const [result, setResult] = useState<ReadinessResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await authedFetch(`/engagements/${engagementRef}/readiness`);
      if (!res.ok) throw new Error('Failed to check what this engagement needs.');
      const body: ReadinessResult = await res.json();
      setResult(body);
    } catch (err: any) {
      setError(err.message || 'Could not check engagement readiness.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, [engagementRef]);

  if (isLoading) return <p className="text-sm text-brand-gray font-mono">Loading…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;

  const missing = result?.missing ?? [];
  const renderChildren = () => (typeof children === 'function' ? children({ missing }) : children);

  // Ready — render the real fulfillment UI (detection findings, outcome
  // detail, whatever this gate is wrapping) with no gate chrome at all.
  if (result?.ready) return <>{renderChildren()}</>;

  // Not ready — block with a specific, non-generic explanation per
  // requirement, matching the DashboardDetectionFindings.tsx empty-state
  // pattern exactly. ALSO invoke the render-prop here (when children is
  // a function) — this is the not-ready case where the parent's own
  // connector UI (e.g. MarqetaConnectForm) actually needs to appear.
  return (
    <div className="space-y-3">
      {missing.map((req) => (
        <div
          key={req.kind}
          className="rounded-xl border border-dashed border-brand-border p-6 text-center"
        >
          <p className="text-sm text-brand-gray mb-3">
            This engagement can't start yet — {req.label.toLowerCase()} before FDW can begin the work.
          </p>
          {REQUIREMENT_ROUTES[req.kind] && (
            <a
              href={REQUIREMENT_ROUTES[req.kind]}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-accent px-4 py-2 text-sm font-medium text-white hover:bg-brand-accentHover"
            >
              <LinkIcon className="size-4" /> {req.label}
            </a>
          )}
        </div>
      ))}
      {typeof children === 'function' && renderChildren()}
    </div>
  );
};